import { createSelector, createSlice } from "@reduxjs/toolkit";
import { ensureArray } from "../common/utils/arrayUtilities";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory } from "../common/reducers/utilities";

const taskListSlice = createSlice({
    name: 'taskLists',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = {};
            ensureArray(action.payload).forEach(list => state.data[list.id] = list);
        },
        addOrUpdate: (state, action) => {
            for (const list of ensureArray(action.payload)) {
                state.data[list.id] = list;
            }
        },
        remove: (state, action) => {
            for (const list of ensureArray(action.payload)) {
                    delete state.data[list.id];
            }
        }
    },
    extraReducers: builder =>  {
        builder.addCase(logout, () => taskListSlice.getInitialState());
    }
});

// #region network operations
const fetchTaskLists = network => network.get('/api/v1/taskLists', taskListSlice.actions.set);
export const insertTaskList = (network, list) => network.post('/api/v1/taskList', list, taskListSlice.actions.addOrUpdate);
export const updateTaskList = (network, delta) => network.put('/api/v1/taskList', delta, taskListSlice.actions.addOrUpdate);
export const removeTaskList = (network, list) => network.delete('/api/v1/taskList/' + encodeURIComponent(list.id), taskListSlice.actions.remove);
// #endregion

// #region permission checks
export const canUpdateTaskList = (credentials, taskList) => credentials.access.hasFull('administrator');
export const canCreateTaskList = (credentials, taskList) => credentials.access.hasFull('administrator');
export const canRemoveTaskList = (credentials, taskList) => credentials.access.hasFull('administrator');
// #endregion

// #region selectors
const selectTaskListData = state => state.taskLists.data;

const makeSelectTaskLists = createSelector([selectTaskListData], lists => Object.values(lists));
const selectTaskLists = state => makeSelectTaskLists(state);
const makeSelectTaskList = createSelector([selectTaskListData, (state, id) => id], (lists, id) => lists[id]);
const selectTaskList = id => state => makeSelectTaskList(state, id);
// #eendregion

// #region listeners
const listen = {
    taskListCreated: (list, { dispatch }) => dispatch(taskListSlice.actions.addOrUpdate(list)),
    taskListUpdated: (list, { dispatch }) => dispatch(taskListSlice.actions.addOrUpdate(list)),
    taskListRemoved: (list, { dispatch }) => dispatch(taskListSlice.actions.remove(list))
}
// #endregion
export const [useTaskLists] = dataHookFactory(selectTaskLists, { run: fetchTaskLists, listen });
export const [useTaskList] = dataHookFactory(selectTaskList, id => ({ run: fetchTaskLists, listen }));

const reducerDetails = { name: taskListSlice.name, reducer: taskListSlice.reducer };
export default reducerDetails;